import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag, Menu, X, Globe } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { getCartCount } = useCart();
  const { language, setLanguage, t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const cartCount = getCartCount();

  const navLinks = [
    { name: t('nav.shop'), path: '/shop' },
    { name: t('nav.about'), path: '/about' },
    { name: t('nav.contact'), path: '/contact' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const handleLogoClick = (e) => {
    e.preventDefault();
    if (location.pathname === '/') {
      // Already on home page, scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // Navigate to home and scroll to top
      navigate('/');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <>
      <nav
        data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled ? 'navbar-glass border-b border-[#EBE5DE]' : 'bg-transparent'
        }`}
      >
        <div className="container-luxury">
          <div className="flex items-center justify-between h-20 md:h-24">
            {/* Logo */}
            <a href="/" onClick={handleLogoClick} className="flex items-center cursor-pointer" data-testid="logo-link">
              <span className="font-heading text-xl md:text-2xl font-semibold tracking-tight text-[#2C2420]">
                Shiva Shakti
              </span>
            </a>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-12">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-link-${link.path.slice(1)}`}
                  className={`font-body text-xs uppercase tracking-widest transition-colors duration-300 ${
                    location.pathname === link.path
                      ? 'text-[#C6A87C]'
                      : 'text-[#2C2420] hover:text-[#C6A87C]'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* Language, Cart & Mobile Menu */}
            <div className="flex items-center gap-4">
              {/* Language Switcher */}
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-1 p-2 text-[#2C2420] hover:text-[#C6A87C] transition-colors duration-300" data-testid="language-switcher">
                  <Globe size={18} />
                  <span className="hidden sm:inline text-xs uppercase">{language}</span>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem 
                    onClick={() => setLanguage('en')}
                    className={language === 'en' ? 'bg-[#F2EFE9]' : ''}
                  >
                    <span className="mr-2">🇬🇧</span> English
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => setLanguage('de')}
                    className={language === 'de' ? 'bg-[#F2EFE9]' : ''}
                  >
                    <span className="mr-2">🇩🇪</span> Deutsch
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Cart */}
              <Link
                to="/cart"
                data-testid="cart-link"
                className="relative p-2 text-[#2C2420] hover:text-[#C6A87C] transition-colors duration-300"
              >
                <ShoppingBag size={22} />
                {cartCount > 0 && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="cart-badge"
                    data-testid="cart-count"
                  >
                    {cartCount}
                  </motion.span>
                )}
              </Link>

              {/* Mobile Menu Button */}
              <button
                data-testid="mobile-menu-button"
                className="md:hidden p-2 text-[#2C2420]"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              >
                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="mobile-menu"
            data-testid="mobile-menu"
          >
            <button
              className="absolute top-6 right-6 p-2 text-[#2C2420]"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <X size={24} />
            </button>
            {navLinks.map((link, index) => (
              <motion.div
                key={link.path}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Link
                  to={link.path}
                  className={
                    location.pathname === link.path
                      ? 'text-[#C6A87C]'
                      : 'text-[#2C2420]'
                  }
                >
                  {link.name}
                </Link>
              </motion.div>
            ))}
            
            {/* Mobile Language Switcher */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="flex gap-4 mt-4"
            >
              <button
                onClick={() => setLanguage('en')}
                className={`text-sm ${language === 'en' ? 'text-[#C6A87C]' : 'text-[#8C8580]'}`}
              >
                🇬🇧 English
              </button>
              <button
                onClick={() => setLanguage('de')}
                className={`text-sm ${language === 'de' ? 'text-[#C6A87C]' : 'text-[#8C8580]'}`}
              >
                🇩🇪 Deutsch
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
